// src/pages/Blog/BlogPostPage.tsx
import React from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import styled from '@emotion/styled';

import LeftArrowButtonIcon from '../../../components/Icons/LeftArrowButtonIcon';
import Page from '../../../components/Page/Page';
import RichTextRenderer from '../../../components/RichTextRenderer/RichTextRenderer';
import { useGetSingleBlogPost } from '../../../hooks/blog/useGetSingleBlogPost';
import { BLOG } from '../../../routes/routeConstants';
import { Post } from '../BlogData';

const defaultCover = 'https://locumspace.co/locumspace-logo.png';

type SinglePostHook = {
  post: Post | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
};

const BlogPostPage: React.FC = () => {
  const { slug, id } = useParams<{ slug: string; id: string }>();
  const { post, loading, error } = useGetSingleBlogPost(id!) as SinglePostHook;
  const navigate = useNavigate();

  // Optional: if the “real” slug from the API doesn’t match the URL, redirect to canonical
  React.useEffect(() => {
    if (post && post.slug !== slug) {
      navigate(`/blog/${post.slug}-${post.id}`, { replace: true });
    }
  }, [post, slug, navigate]);

  console.log(post);

  if (loading) return <Full>Fetching Blog Post…</Full>;
  if (error) return <Full>Error: {error}</Full>;
  if (!post) return <Navigate to={BLOG} replace />;

  return (
    <Page
      title={`Locumspace | Blog – ${post.title}`}
      description={post.excerpt}
      author={post.authorName}
      keywords={post.keywords ?? []}
      url={`${window.location.origin}/blog/${post.slug}-${post.id}`}
      image={post.coverImageUrl || defaultCover}
      type='article'
      siteName='Locumspace'
      noindex={false}
      lang='en'
    >
      <Back onClick={() => navigate(`/${BLOG}`)}>
        <LeftArrowButtonIcon /> Back to list
      </Back>
      <Article>
        <Header>
          <h1>{post.title}</h1>
          <div>
            <CoverImage
              src={post.coverImageUrl ?? defaultCover}
              alt={post.coverImageUrl ? `Cover for ${post.title}` : ''}
              onError={(e) => {
                e.currentTarget.src = defaultCover;
                e.currentTarget.alt = `Cover for ${post.title}`;
              }}
            />
          </div>

          <Meta>
            By <strong>{post.authorName}</strong> on{' '}
            <time dateTime={post.publishDate}>
              {new Date(post.publishDate).toLocaleDateString()}
            </time>
          </Meta>
        </Header>

        <Content>
          <RichTextRenderer document={post.body} />
        </Content>

        <Footer>
          <ShareHeading>Share this article:</ShareHeading>
          <ShareList>
            <li>
              <a
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(
                  post.title
                )}&url=${encodeURIComponent(window.location.href)}`}
                target='_blank'
                rel='noopener noreferrer'
              >
                Twitter
              </a>
            </li>
            <li>
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                  window.location.href
                )}`}
                target='_blank'
                rel='noopener noreferrer'
              >
                Facebook
              </a>
            </li>
          </ShareList>
        </Footer>
      </Article>
    </Page>
  );
};

export default BlogPostPage;

// === Styled Components ===

const Article = styled.article`
  max-width: 800px;
  margin: 2rem auto;
  padding: 0 1rem;
`;

const Header = styled.header`
  text-align: center;
  margin-bottom: 2rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  h1 {
    margin-bottom: 1rem;
    font-size: 2rem;
  }

  div {
    max-width: 50rem;
    max-height: 25rem;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    overflow: hidden;
  }
`;

const CoverImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: 8px;
  margin-bottom: 1rem;
  object-fit: contain;
`;

const Meta = styled.p`
  font-size: 0.9rem;
  color: #666;
  margin-top: 2rem;

  strong {
    color: ${({ theme }) => theme.palette.blackBlackMain};
  }
`;

const Content = styled.section`
  line-height: 1.7;
  margin-bottom: 2rem;

  p {
    margin-bottom: 1rem;
  }
`;

const Footer = styled.footer`
  border-top: 1px solid #e0e0e0;
  padding-top: 1.5rem;
`;

const ShareHeading = styled.h2`
  font-size: 1rem;
  margin-bottom: 0.5rem;
`;

const ShareList = styled.ul`
  list-style: none;
  padding: 0;
  display: flex;
  gap: 1rem;

  a {
    text-decoration: none;
    font-weight: 500;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const Back = styled.p`
  position: absolute;
  left: 7rem;
  color: ${({ theme }) => theme.palette.mainBlue};
  font-weight: 500;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  cursor: pointer;
  transition: transform 0.3s ease-in-out;

  svg {
    width: 1.5rem;
  }

  :hover {
    transform: translateY(-5px);
  }

  @media (max-width: 480px) {
    left: 1.5rem;
  }
`;

const Full = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 7rem;
`;
